/**
 * A-GTCC-VIEW-ALIN1 — typed client for the "Customer Launch" (launch) view.
 *
 * Reference: `docs/design/GTCC_REFERENCE_2026-09-01.html:900-917`.
 *
 * ## Measured, not invented
 *
 * The instruction says "bestehendes customer-launch-overview-Modul der API
 * (Surface customer-launch-overview; Endpunkt messen, wiederverwenden)". The
 * measurement:
 *
 *   - `GET /v1/admin/customer-launch-overview`
 *     (`services/api/src/modules/admin/customer-launch-overview.routes.ts:21`)
 *     is platform-owner gated via `requirePlatformOwner("customer-launch-
 *     overview")` and read-only. Its response is
 *     `CustomerLaunchOverviewResponse`
 *     (`customer-launch-overview.service.ts:47`): `items`, `total`, `capped`.
 *   - Each item carries exactly the six columns the reference draws — tenant,
 *     type · subscription status, admin/vehicle/device counts, the latest mail
 *     delivery status and the `overall` verdict.
 *
 * So NO new endpoint is added for this view. This module is the typed GTCC
 * client in front of the existing surface: same shapes as
 * `../views/launch/useCustomerLaunch.ts` (which keeps serving the older
 * `GtccCustomerLaunchView` until `D-GTCC-SHELL1` retires it), plus the
 * `LaunchResult` union the GTCC views share, so the view can distinguish
 * "forbidden" from "route not there" from "transport broke" without ever
 * seeing an `ApiError`.
 *
 * The four reference tiles READY / NEEDS ACTION / BLOCKED / MANUAL CHECK are
 * counted from `items` by {@link summariseLaunchRows} — the endpoint sends no
 * pre-aggregated tile numbers, and counting the rows it did send is a
 * measurement, not an invention. `total`/`capped` are surfaced so a capped
 * response says so instead of presenting a partial count as complete.
 */
import { apiGet, ApiError } from "../../lib/api.js";

/* --------------------------------------------------------------- paths */

export const LAUNCH_OVERVIEW_PATH = "/v1/admin/customer-launch-overview";

/* ---------------------------------------------------------------- types */

/** The closed verdict enum of the launch service. */
export const LAUNCH_STATUSES = ["ready", "needs_action", "blocked", "manual"] as const;
export type LaunchStatus = (typeof LAUNCH_STATUSES)[number];

/** Mirrors `CustomerLaunchOverviewItem` field for field. */
export interface LaunchRowDto {
  readonly tenantId: string;
  readonly displayName: string | null;
  readonly type: string;
  readonly subscriptionStatus: string;
  readonly overall: LaunchStatus;
  readonly invitationCount: number;
  readonly activeAdminCount: number;
  readonly vehicleCount: number;
  readonly deviceCount: number;
  readonly latestEmailDeliveryStatus: string | null;
  readonly latestEmailDeliveryProvider: string | null;
}

/** Mirrors `CustomerLaunchOverviewResponse`. */
export interface LaunchOverviewDto {
  readonly items: ReadonlyArray<LaunchRowDto>;
  readonly total: number;
  readonly capped: boolean;
}

/* --------------------------------------------------------------- result */

export type LaunchResult<T> =
  | { readonly ok: true; readonly data: T }
  | { readonly ok: false; readonly kind: "disabled"; readonly httpStatus: 404 }
  | { readonly ok: false; readonly kind: "forbidden"; readonly httpStatus: 403 }
  | {
      readonly ok: false;
      readonly kind: "upstream";
      readonly httpStatus: number;
      readonly requestId: string | undefined;
    }
  | { readonly ok: false; readonly kind: "transport_error"; readonly message: string };

export async function fetchLaunchOverview(
  signal?: AbortSignal,
): Promise<LaunchResult<LaunchOverviewDto>> {
  try {
    return {
      ok: true,
      data: await apiGet<LaunchOverviewDto>(LAUNCH_OVERVIEW_PATH, undefined, signal),
    };
  } catch (err) {
    if (err instanceof ApiError) {
      const { status, requestId } = err;
      if (status === 404) return { ok: false, kind: "disabled", httpStatus: 404 };
      if (status === 403) return { ok: false, kind: "forbidden", httpStatus: 403 };
      return { ok: false, kind: "upstream", httpStatus: status, requestId };
    }
    const message = err instanceof Error ? err.message : "customer launch fetch failed";
    return { ok: false, kind: "transport_error", message };
  }
}

/* ------------------------------------------------------------- ordering */

/** Worst first, so the tenants that block a launch sit at the top. */
const LAUNCH_STATUS_WEIGHT: Readonly<Record<LaunchStatus, number>> = {
  blocked: 3,
  needs_action: 2,
  manual: 1,
  ready: 0,
};

export function sortLaunchRows(rows: ReadonlyArray<LaunchRowDto>): ReadonlyArray<LaunchRowDto> {
  return [...rows].sort(
    (left, right) =>
      LAUNCH_STATUS_WEIGHT[right.overall] - LAUNCH_STATUS_WEIGHT[left.overall] ||
      (left.displayName ?? left.tenantId).localeCompare(right.displayName ?? right.tenantId),
  );
}

/**
 * The four tiles. A status the server adds later is simply not counted rather
 * than thrown on — the tile row must never be the reason a view crashes.
 */
export function summariseLaunchRows(
  rows: ReadonlyArray<LaunchRowDto>,
): Readonly<Record<LaunchStatus, number>> {
  const counts: Record<LaunchStatus, number> = {
    ready: 0,
    needs_action: 0,
    blocked: 0,
    manual: 0,
  };
  for (const row of rows) {
    if (row.overall in counts) counts[row.overall] += 1;
  }
  return counts;
}

/* ----------------------------------------------------------- query keys */

export const LAUNCH_OVERVIEW_KEY = ["gtcc", "launch", "gtcc-overview"] as const;

export const LAUNCH_QUERY_KEYS = {
  overview: LAUNCH_OVERVIEW_KEY,
} as const;
